import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Play, Crown, Clock, Gamepad2, Edit3, Check, X, Info } from 'lucide-react';
import { User } from './HeatedGame';
import '../components/css/lobby.css'

interface LobbyProps {
    users: User[];
    currentUser: User;
    roomCode: string | string[] | null;
    isCreator: boolean;
    creatorId: string | null;
    onStartGame: (timerLength: number) => void;
    onChangeName: (newName: string) => void;
}

const Lobby: React.FC<LobbyProps> = ({ users, currentUser, roomCode, isCreator, creatorId, onStartGame, onChangeName }) => {
    const [isEditingName, setIsEditingName] = useState(false);
    const [editNameValue, setEditNameValue] = useState(currentUser.name);
    const [timerLength, setTimerLength] = useState(7);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.6,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    const playerCardVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: {
            scale: 1,
            opacity: 1,
            transition: {
                duration: 0.4,
                ease: "easeOut"
            }
        },
        exit: {
            scale: 0.8,
            opacity: 0,
            transition: {
                duration: 0.3
            }
        }
    };

    const handleStartNameEdit = () => {
        setIsEditingName(true);
        setEditNameValue(currentUser.name);
    };

    const handleSaveName = () => {
        if (editNameValue.trim() && editNameValue.trim() !== currentUser.name) {
            onChangeName(editNameValue.trim());
        }
        setIsEditingName(false);
    };

    const handleCancelNameEdit = () => {
        setIsEditingName(false);
        setEditNameValue(currentUser.name);
    };

    const handleNameKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSaveName();
        } else if (e.key === 'Escape') {
            handleCancelNameEdit();
        }
    };

    return (
        <div className="lobby-container">
            <motion.div
                className="lobby-content"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Header */}
                <motion.div className="lobby-header" variants={itemVariants}>
                    <div className="lobby-title-section">
                        <Gamepad2 className="lobby-icon" />
                        <div>
                            <h1>Game Lobby</h1>
                            <p>Room Code: <span className="room-code">{roomCode}</span></p>
                        </div>
                    </div>
                    <div className="player-count">
                        <Users className="count-icon" />
                        <span>{users.length}/5 Players</span>
                    </div>
                </motion.div>

                {/* Players List */}
                <motion.div className="players-section" variants={itemVariants}>
                    <h2>Players in Lobby</h2>
                    <div className="players-grid">
                        <AnimatePresence>
                            {users.map((user, index) => (
                                <motion.div
                                    key={user.id}
                                    className={`player-card ${user.id === currentUser.id ? 'current-user' : ''}`}
                                    variants={playerCardVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    layout
                                >
                                    <div className="player-info">
                                        <div className="player-avatar">
                                            {user.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="player-details">
                                            <div className="player-name-section">
                                                {user.id === currentUser.id && isEditingName ? (
                                                    <div className="name-edit-container">
                                                        <input
                                                            type="text"
                                                            value={editNameValue}
                                                            onChange={(e) => setEditNameValue(e.target.value)}
                                                            onKeyDown={handleNameKeyPress}
                                                            className="name-edit-input"
                                                            maxLength={20}
                                                            autoFocus
                                                        />
                                                        <div className="name-edit-buttons">
                                                            <motion.button
                                                                className="name-edit-btn save"
                                                                onClick={handleSaveName}
                                                                whileHover={{ scale: 1.05 }}
                                                                whileTap={{ scale: 0.95 }}
                                                            >
                                                                <Check size={14} />
                                                            </motion.button>
                                                            <motion.button
                                                                className="name-edit-btn cancel"
                                                                onClick={handleCancelNameEdit}
                                                                whileHover={{ scale: 1.05 }}
                                                                whileTap={{ scale: 0.95 }}
                                                            >
                                                                <X size={14} />
                                                            </motion.button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="name-display-container">
                                                        <span className="player-name">{user.name}</span>
                                                        {user.id === currentUser.id && !isEditingName && (
                                                            <motion.button
                                                                className="edit-name-btn"
                                                                onClick={handleStartNameEdit}
                                                                whileHover={{ scale: 1.05 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                title="Edit your name"
                                                            >
                                                                <Edit3 size={14} />
                                                            </motion.button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <span className="player-status">
                                                {user.id === creatorId && <Crown className="crown-icon" />}
                                                {user.id === creatorId ? 'Room Creator' : 'Player'}
                                            </span>
                                        </div>
                                    </div>
                                    {user.id === currentUser.id && (
                                        <div className="you-indicator">You</div>
                                    )}
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </motion.div>

                {/* Waiting/Start Section */}
                <motion.div className="action-section" variants={itemVariants}>
                    {isCreator ? (
                        // In Lobby component

                        <div className="creator-section">
                            <p className="creator-message">
                                You are the room creator. Start the game when ready!
                            </p>
                            <div className="game-settings">
                          <label>
                            Response Timer (seconds):
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input
                                type="number"
                                min="5"
                                value={timerLength}
                                onChange={(e) => setTimerLength(Math.max(5, parseInt(e.target.value)))}
                              />
                              <div
                                className="tooltip-container"
                                title="Time limit for players to respond when they have a No, Thanks or a card they can stack. After this time, they must draw cards and commence their turn without rejecting the play."
                              >
                                <Info
                                  size={16}
                                  style={{ cursor: 'help', color: 'var(--gray-300)' }}
                                />
                              </div>
                            </div>
                          </label>
                        </div>
                            <motion.button
                                className="start-game-btn"
                                onClick={() => onStartGame(timerLength)}
                                disabled={users.length < 2}
                                whileHover={users.length >= 2 ? { scale: 1.05 } : {}}
                                whileTap={users.length >= 2 ? { scale: 0.95 } : {}}
                            >
                                <Play className="btn-icon" />
                                {users.length < 2 ? 'Need at least 2 players' : 'Start Game'}
                            </motion.button>
                        </div>
                    ) : (
                        <div className="waiting-section">
                            <motion.div
                                className="waiting-indicator"
                                animate={{
                                    scale: [1, 1.1, 1],
                                    opacity: [0.7, 1, 0.7]
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            >
                                <Clock className="waiting-icon" />
                            </motion.div>
                            <p className="waiting-message">
                                Waiting for the room creator to start the game...
                            </p>
                        </div>
                    )}
                </motion.div>

                {/* Instructions */}
                <motion.div className="instructions-section" variants={itemVariants}>
                    <h3>How to play Heated:</h3>
                    <ul>
                        <li>Play cards that match the color or number of the top card</li>
                        <li>Use action cards strategically to gain advantage</li>
                        <li>Say "HEATED!" when you have one card left</li>
                        <li>First player to empty their hand wins!</li>
                    </ul>
                </motion.div>
            </motion.div>

            <style>{`

            `}</style>
        </div>
    );
};

export default Lobby;
